import { useCallback, useEffect, MutableRefObject } from 'react';
import {
	DlobWebsocketClient,
	MarketId,
	ENUM_UTILS,
} from '@velocity-exchange/common';
import useOrderbookDisplayStore, {
	OrderbookSource,
	getBestAvailableOrderbookSource,
} from 'src/stores/orderbook/useOrderbookDisplayStore';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import { useSubscriptionTabRecovery } from 'src/hooks/utils/useSubscriptionTabRecovery';
import { useShallow } from 'zustand/react/shallow';
import {
	FALLBACK_COOLDOWN_MS,
	MAX_RECOVERY_ATTEMPTS,
	TAB_INACTIVE_DISCONNECT_DELAY_MS,
	RECOVERY_CHECK_INTERVAL_MS,
} from '../constants';

interface UseTabVisibilityAndRecoveryParams {
	selectedMarketId: MarketId | undefined;
	clientRef: MutableRefObject<DlobWebsocketClient | null>;
	subscriptionRef: MutableRefObject<{ unsubscribe: () => void } | null>;
	currentSubscriptionMarketIdRef: MutableRefObject<MarketId | null>;
	currentSubscriptionGroupingRef: MutableRefObject<number | null>;
	lastFallbackTimestamp: MutableRefObject<number>;
	/**
	 * Callback to perform full recovery. This callback may depend on
	 * destroyCurrentPipeline$ internally, but the tab visibility effect
	 * should NOT directly depend on destroyCurrentPipeline$.
	 */
	performRecovery: () => void;
	/**
	 * Callback for tab inactive cleanup. Called inside the timeout handler.
	 */
	onTabInactiveCleanup: () => void;
	setSubscriptionRestartKey: React.Dispatch<React.SetStateAction<number>>;
}

/**
 * Orderbook adapter over the shared `useSubscriptionTabRecovery` module.
 *
 * Maps the orderbook transport into the shared tab-recovery interface:
 * - health = orderbook source is WEBSOCKET
 * - recovery enabled = auto source switching not disabled
 * - teardown = unsubscribe + destroy DLOB client + clear market/grouping refs
 * - reconnect = teardown + switch back to best available source + bump the
 *   subscription restart key (deliberately NOT performRecovery: the restart
 *   key path lets useOrderbookDataPipeline recreate its Subject cleanly)
 *
 * FE-4066: destroyCurrentPipeline$ must never be a dependency of the tab
 * visibility handling. Having it in the dependency array caused a race
 * condition during websocket recovery:
 *
 * 1. Websocket times out -> falls back to polling
 * 2. Recovery triggers -> destroyCurrentPipeline$.next() called
 * 3. subscriptionRestartKey increments -> new Subject created in useOrderbookDataPipeline
 * 4. New Subject reference causes the visibility effect to re-run
 * 5. Effect re-run interferes with the ongoing recovery process
 * 6. Result: websocket never recovers, stuck in polling mode
 *
 * The shared module enforces the fix structurally — it never sees the Subject
 * and holds all callbacks behind refs, so callback identity churn (from the
 * Subject being recreated) cannot re-fire its effects.
 */
export function useTabVisibilityAndRecovery({
	selectedMarketId,
	clientRef,
	subscriptionRef,
	currentSubscriptionMarketIdRef,
	currentSubscriptionGroupingRef,
	lastFallbackTimestamp,
	performRecovery,
	onTabInactiveCleanup,
	setSubscriptionRestartKey,
}: UseTabVisibilityAndRecoveryParams): void {
	const { orderbookSource, disableAutoSourceSwitching } =
		useOrderbookDisplayStore(
			useShallow((s) => ({
				orderbookSource: s.orderbookSource,
				disableAutoSourceSwitching: s.disableAutoSourceSwitching,
			}))
		);

	const setOrderbookStore = useOrderbookDisplayStore((s) => s.set);
	const selectedMarketIdRef = useSyncedRef(selectedMarketId);
	const disableAutoSourceSwitchingRef = useSyncedRef(
		disableAutoSourceSwitching
	);

	/** Tear down the websocket transport and clear subscription tracking. */
	const teardownTransport = useCallback(() => {
		if (subscriptionRef.current) {
			subscriptionRef.current.unsubscribe();
			subscriptionRef.current = null;
		}
		if (clientRef.current) {
			clientRef.current.destroy();
			clientRef.current = null;
		}
		currentSubscriptionMarketIdRef.current = null;
		currentSubscriptionGroupingRef.current = null;
	}, [
		subscriptionRef,
		clientRef,
		currentSubscriptionMarketIdRef,
		currentSubscriptionGroupingRef,
	]);

	const handleTabInactiveCleanup = useCallback(() => {
		onTabInactiveCleanup();
		teardownTransport();
		// Don't increment subscriptionRestartKey here — it would trigger
		// the main useEffect and immediately reconnect. Recovery happens
		// when the tab becomes active again.
	}, [onTabInactiveCleanup, teardownTransport]);

	const handleTabActiveReconnect = useCallback(() => {
		if (!selectedMarketIdRef.current) return;

		teardownTransport();

		if (!disableAutoSourceSwitchingRef.current) {
			setOrderbookStore((s) => {
				s.orderbookSource = getBestAvailableOrderbookSource();
			});
		}

		setSubscriptionRestartKey((prev) => prev + 1);
	}, [
		selectedMarketIdRef,
		teardownTransport,
		disableAutoSourceSwitchingRef,
		setOrderbookStore,
		setSubscriptionRestartKey,
	]);

	// Eager recovery is only safe while a subscription is actually active —
	// mid-recovery (refs nulled) the periodic check owns retrying.
	const canAttemptEagerRecovery = useCallback(
		() =>
			Boolean(
				selectedMarketIdRef.current && currentSubscriptionMarketIdRef.current
			),
		[selectedMarketIdRef, currentSubscriptionMarketIdRef]
	);

	useSubscriptionTabRecovery({
		isHealthy: ENUM_UTILS.match(orderbookSource, OrderbookSource.WEBSOCKET),
		recoveryEnabled: !disableAutoSourceSwitching,
		lastFallbackTimestamp,
		performRecovery,
		onTabInactiveCleanup: handleTabInactiveCleanup,
		onTabActiveReconnect: handleTabActiveReconnect,
		canAttemptEagerRecovery,
		timing: {
			fallbackCooldownMs: FALLBACK_COOLDOWN_MS,
			recoveryCheckIntervalMs: RECOVERY_CHECK_INTERVAL_MS,
			tabInactiveDisconnectDelayMs: TAB_INACTIVE_DISCONNECT_DELAY_MS,
			maxRecoveryAttempts: MAX_RECOVERY_ATTEMPTS,
		},
	});

	// Handle manual source changes (when auto-switching is disabled): if the
	// user pinned the websocket source and switches market, force a restart so
	// the subscription follows the selected market.
	useEffect(() => {
		if (!disableAutoSourceSwitching) return;

		if (
			ENUM_UTILS.match(orderbookSource, OrderbookSource.WEBSOCKET) &&
			selectedMarketId &&
			!currentSubscriptionMarketIdRef.current?.equals(selectedMarketId)
		) {
			currentSubscriptionMarketIdRef.current = null;
			currentSubscriptionGroupingRef.current = null;
			setSubscriptionRestartKey((prev) => prev + 1);
		}
	}, [
		disableAutoSourceSwitching,
		orderbookSource,
		selectedMarketId,
		currentSubscriptionMarketIdRef,
		currentSubscriptionGroupingRef,
		setSubscriptionRestartKey,
	]);
}
